import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Building2,
  Wind,
  Droplets,
  Home,
  Sofa,
  Flame,
  Sparkles,
  BedDouble,
  Grid,
  GraduationCap,
  UtensilsCrossed,
  Heart,
  ArrowRight,
} from "lucide-react";

const Servicespage = () => {
  const services = [
    {
      icon: Building2,
      title: "Commercial Cleaning",
      description:
        "Professional cleaning solutions for offices, retail spaces, and commercial buildings.",
      features: [
        "Regular Cleaning",
        "Floor Maintenance",
        // "Waste Management",
        "Sanitization",
      ],
    },
    {
      icon: Wind,
      title: "Window Cleaning",
      description:
        "Crystal clear results for all types of windows, including high-rise buildings.",
      features: [
        "Interior/Exterior",
        "High-Rise Access",
        "Screen Cleaning",
        "Track Cleaning",
      ],
    },
    {
      icon: Droplets,
      title: "High Pressure Washing",
      description: "Powerful cleaning for exterior surfaces and tough stains.",
      features: ["Driveways", "Walls", "Patios", "Commercial Spaces"],
    },
    {
      icon: Home,
      title: "End-of-Lease Cleaning",
      description: "Comprehensive cleaning to ensure full bond return.",
      features: ["Full Property", "Appliances", "Windows", "Carpets"],
    },
    {
      icon: Sofa,
      title: "Carpet / Rug and Upholstery",
      description:
        "Deep cleaning for all types of fabrics and floor coverings.",
      features: [
        "Stain Removal",
        "Deodorizing",
        "Steam Cleaning",
        "Fabric Protection",
      ],
    },
    {
      icon: Flame,
      title: "Oven / BBQ & Range Hood",
      description: "Thorough cleaning of cooking appliances and ventilation.",
      features: [
        "Degreasing",
        "Parts Cleaning",
        "Filter Replacement",
        //  "Sanitization"
      ],
    },
    {
      icon: Sparkles,
      title: "Deep Cleaning",
      description:
        "Intensive cleaning service for thorough property maintenance.",
      features: [
        "All Surfaces",
        "Hidden Areas",
        // "Disinfection",
        "Detailed Clean",
      ],
    },
    {
      icon: BedDouble,
      title: "Airbnb Cleaning",
      description: "Specialized cleaning for short-term rental properties.",
      features: ["Quick Turnover", "Linen Service", "Restocking", "Inspection"],
    },
    {
      icon: Grid,
      title: "Tiles & Grout Cleaning",
      description: "Restoring the original beauty of your tiled surfaces.",
      features: [
        "Deep Scrubbing",
        "Mould Cleaning",
        // "Sealing",
        // "Regrouting",
        // "Restoration"
      ],
    },
    {
      icon: GraduationCap,
      title: "School Cleaning",
      description: "Creating clean and healthy learning environments.",
      features: [
        "Periodical Cleaning",
        "Classrooms",
        "Common Areas",
        "Sanitization",
        // "Daily Service"
      ],
    },
    {
      icon: UtensilsCrossed,
      title: "Commercial Kitchen Cleaning",
      description:
        "Professional cleaning for commercial food preparation areas.",
      features: [
        "Equipment",
        "Surfaces",
        // "Compliance",
        "Deep Clean",
      ],
    },
    {
      icon: Heart,
      title: "Medical Centers & Age Care",
      description: "Specialized cleaning for healthcare facilities.",
      features: [
        // "Sterilization",
        // "Bio-Waste",
        // "Compliance",
        "Deep Cleaning",
        "Regular Cleaning",
        "24/7 Service",
      ],
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white via-gray-50 to-white">
      {/* Hero Section with enhanced gradient */}
      <div className="relative mb-20">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/20 to-primary/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Professional{" "}
              <span className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                Cleaning Services
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cleaning solutions tailored to your specific needs,
              delivered by trained professionals with attention to detail.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="relative p-[1px] rounded-2xl bg-gradient-to-br from-primary/20 via-secondary/20 to-transparent hover:from-primary/30 hover:via-secondary/30 hover:to-transparent transition-all duration-500">
                  <div className="relative bg-white p-8 rounded-2xl">
                    {/* Subtle gradient hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white rounded-2xl group-hover:from-primary/5 group-hover:to-secondary/5 transition-all duration-500" />

                    {/* Content */}
                    <div className="relative">
                      <div className="flex items-center mb-6">
                        <div className="p-2 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                          <IconComponent className="h-8 w-8 text-secondary group-hover:text-primary transition-colors duration-300" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 ml-4 group-hover:text-primary transition-colors duration-300">
                          {service.title}
                        </h3>
                      </div>

                      <p className="text-gray-600 mb-6 group-hover:text-gray-700 transition-colors duration-300">
                        {service.description}
                      </p>

                      <ul className="space-y-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-center text-gray-600 group-hover:text-gray-700 transition-colors duration-300"
                          >
                            <div className="h-1.5 w-1.5 bg-gradient-to-r from-primary to-secondary rounded-full mr-3 group-hover:scale-125 transition-all duration-300" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <Link
                        to={`/contact?service=${encodeURIComponent(
                          service.title
                        )}`}
                        className="inline-flex items-center text-primary hover:text-secondary transition-colors duration-300"
                      >
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                          Get Quote
                        </span>
                        <ArrowRight className="ml-2 h-4 w-4 text-secondary group-hover:text-primary transition-colors duration-300" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA with enhanced gradient */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-20"
        >
          <div className="relative p-[1px] rounded-2xl bg-gradient-to-r from-primary via-secondary to-primary">
            <div className="bg-white rounded-2xl px-8 py-12 relative overflow-hidden">
              {/* Background gradient effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white opacity-90" />

              <div className="relative">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Need a Custom Cleaning Solution?
                </h2>
                <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                  Contact us to discuss your specific requirements. We'll create
                  a tailored cleaning plan that meets your needs and budget.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-3 rounded-xl text-white bg-gradient-to-r from-primary via-secondary to-primary hover:from-primary-dark hover:via-secondary hover:to-primary-dark transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  Contact Us Now
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Servicespage;
